/**
 * An alert returned by the legacy unified active-alerts endpoint.
 */
export type LegacyFemaAlert = {
  message_id?: string | number;
  fema_identifier?: string;
  message?: string;
  smpp_message?: string;
  description?: string;
  nws_office?: string;
  severity?: number;
  issue_time_utc?: string;
  valid_until_time_utc?: string;
};

/**
 * Identifies FEMA alerts by their identifier, office, or legacy message marker.
 *
 * @param value - A provider alert returned by the active-alerts endpoint.
 * @returns True when the alert contains a legacy FEMA marker.
 */
export const isLegacyFemaAlert = (value: unknown): value is LegacyFemaAlert => {
  if (!value || typeof value !== 'object') return false;
  const alert = value as LegacyFemaAlert;
  return (
    (typeof alert.fema_identifier === 'string' && alert.fema_identifier.length > 0) ||
    alert.nws_office === 'FEMA IPAWS' ||
    (typeof alert.message === 'string' && /\[FEMA:[^\]]+\]/.test(alert.message))
  );
};

/**
 * Identifies canonical and legacy FEMA alerts.
 * Legacy unified alerts may not include the canonical `alertSource` field.
 *
 * @param value - An alert returned by an alert provider.
 * @returns True when the alert has the FEMA source or a legacy FEMA marker.
 */
export const isFemaAlert = (value: unknown): boolean => {
  if (isLegacyFemaAlert(value)) return true;
  if (!value || typeof value !== 'object') return false;

  return 'alertSource' in value && value.alertSource === 'fema';
};
